import React, { Suspense, useMemo, useRef } from 'react';
import {
  ResponsiveGridLayout,
  useContainerWidth,
  verticalCompactor,
} from 'react-grid-layout';
import type { LayoutItem, Layout } from 'react-grid-layout';
import {
  Box,
  IconButton,
  Paper,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { getWidgetById } from './registry';
import { GRID_COLS, ROW_HEIGHT, GRID_MARGIN } from './types';
import type { WidgetLayoutItem } from './types';

import 'react-grid-layout/css/styles.css';

interface WidgetGridProps {
  widgets: WidgetLayoutItem[];
  isEditMode: boolean;
  onLayoutChange: (widgets: WidgetLayoutItem[]) => void;
  onRemove: (key: string) => void;
  onPreferredHeight?: (key: string, rows: number) => void;
}

const WidgetGrid: React.FC<WidgetGridProps> = ({
  widgets,
  isEditMode,
  onLayoutChange,
  onRemove,
  onPreferredHeight,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { width, containerRef } = useContainerWidth();
  const mountedRef = useRef(false);

  const layout: Layout = useMemo(
    () =>
      widgets.map((w): LayoutItem => {
        const def = getWidgetById(w.widget_id);
        return {
          i: w.i,
          x: w.x,
          y: w.y,
          w: w.w,
          h: w.h,
          minW: def?.constraints.minW ?? 2,
          maxW: def?.constraints.maxW ?? 12,
          minH: def?.constraints.minH ?? 2,
          maxH: def?.constraints.maxH ?? 8,
          isDraggable: isEditMode,
          isResizable: isEditMode,
        };
      }),
    [widgets, isEditMode],
  );

  const handleLayoutChange = (newLayout: Layout) => {
    if (!mountedRef.current) {
      mountedRef.current = true;
      return;
    }
    if (!isEditMode) return;
    const updated: WidgetLayoutItem[] = newLayout
      .map((l) => {
        const existing = widgets.find((w) => w.i === l.i);
        if (!existing) return null;
        return {
          widget_id: existing.widget_id,
          i: l.i,
          x: l.x,
          y: l.y,
          w: l.w,
          h: l.h,
        };
      })
      .filter((w): w is WidgetLayoutItem => w !== null);
    onLayoutChange(updated);
  };

  return (
    <Box
      ref={containerRef}
      sx={{
        position: 'relative',
        ...(isEditMode && {
          '& .react-grid-item.react-grid-placeholder': {
            bgcolor: alpha(theme.palette.primary.main, 0.15),
            borderRadius: '12px',
            border: `2px dashed ${alpha(theme.palette.primary.main, 0.4)}`,
            opacity: '1 !important',
            background: `${alpha(theme.palette.primary.main, 0.08)} !important`,
          },
          '& .react-grid-item > .react-resizable-handle': {
            width: 20,
            height: 20,
          },
          '& .react-grid-item > .react-resizable-handle::after': {
            borderColor: alpha(theme.palette.primary.main, 0.5),
            width: 7,
            height: 7,
          },
        }),
      }}
    >
      {isEditMode && <GridOverlay isDark={isDark} />}

      <ResponsiveGridLayout
        width={width}
        layouts={{ lg: layout }}
        breakpoints={{ lg: 1200, md: 900, sm: 600, xs: 0 }}
        cols={{ lg: GRID_COLS, md: GRID_COLS, sm: 6, xs: 1 }}
        rowHeight={ROW_HEIGHT}
        margin={GRID_MARGIN}
        containerPadding={[0, 0]}
        dragConfig={{
          enabled: isEditMode,
          handle: '.widget-drag-handle',
          bounded: false,
          threshold: 3,
        }}
        resizeConfig={{
          enabled: isEditMode,
          handles: ['se'],
        }}
        onLayoutChange={(currentLayout) => handleLayoutChange(currentLayout)}
        compactor={verticalCompactor}
      >
        {widgets.map((widget) => {
          const definition = getWidgetById(widget.widget_id);
          if (!definition) return <div key={widget.i} />;

          const IconComp = definition.icon;
          const WidgetComponent = definition.component;

          return (
            <div key={widget.i}>
              <Paper
                elevation={0}
                sx={{
                  height: '100%',
                  p: 2,
                  bgcolor: isDark ? '#212744' : '#ffffff',
                  border: `1px solid ${isDark ? '#2e3560' : '#dfe3e8'}`,
                  borderRadius: 3,
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden',
                  transition: 'border-color 0.2s, box-shadow 0.2s',
                  ...(isEditMode && {
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                    '&:hover': {
                      boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.15)}`,
                    },
                  }),
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, minHeight: 24, minWidth: 0 }}>
                  {isEditMode && (
                    <Box
                      className="widget-drag-handle"
                      sx={{
                        cursor: 'grab',
                        display: 'flex',
                        alignItems: 'center',
                        color: isDark ? '#667788' : '#6b7c8d',
                        '&:active': { cursor: 'grabbing' },
                      }}
                    >
                      <DragIndicatorIcon sx={{ fontSize: 18 }} />
                    </Box>
                  )}
                  <IconComp sx={{ fontSize: 16, color: isDark ? '#667788' : '#6b7c8d' }} />
                  <Typography
                    noWrap
                    title={definition.name}
                    sx={{
                      fontSize: '0.78rem',
                      fontWeight: 700,
                      color: isDark ? '#8899aa' : '#5f7082',
                      flex: 1,
                      minWidth: 0,
                    }}
                  >
                    {definition.name}
                  </Typography>
                  {isEditMode && (
                    <IconButton
                      size="small"
                      onClick={() => onRemove(widget.i)}
                      sx={{
                        width: 24,
                        height: 24,
                        color: isDark ? '#667788' : '#6b7c8d',
                        '&:hover': { color: '#ef5350', bgcolor: alpha('#ef5350', 0.1) },
                      }}
                    >
                      <CloseIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  )}
                </Box>

                <Box sx={{ flex: 1, overflow: 'hidden' }}>
                  <Suspense
                    fallback={
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                        <Typography sx={{ color: isDark ? '#667788' : '#6b7c8d', fontSize: '0.85rem' }}>
                          Laden...
                        </Typography>
                      </Box>
                    }
                  >
                    <WidgetComponent
                      w={widget.w}
                      h={widget.h}
                      onPreferredHeight={
                        isEditMode || !onPreferredHeight
                          ? undefined
                          : (rows) => onPreferredHeight(widget.i, rows)
                      }
                    />
                  </Suspense>
                </Box>
              </Paper>
            </div>
          );
        })}
      </ResponsiveGridLayout>
    </Box>
  );
};

const GridOverlay: React.FC<{ isDark: boolean }> = ({ isDark }) => {
  const lineColor = isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)';
  const rowLineColor = isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)';
  const rowStep = ROW_HEIGHT + GRID_MARGIN[1];

  return (
    <Box
      sx={{
        position: 'absolute',
        inset: 0,
        pointerEvents: 'none',
        zIndex: 0,
        display: 'grid',
        gridTemplateColumns: `repeat(${GRID_COLS}, 1fr)`,
        gap: `${GRID_MARGIN[0]}px`,
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          zIndex: 1,
          backgroundImage: `repeating-linear-gradient(
            to bottom,
            transparent 0,
            transparent ${ROW_HEIGHT}px,
            ${rowLineColor} ${ROW_HEIGHT}px,
            ${rowLineColor} ${ROW_HEIGHT + 1}px,
            transparent ${ROW_HEIGHT + 1}px,
            transparent ${rowStep}px
          )`,
        }}
      />
      {Array.from({ length: GRID_COLS }).map((_, i) => (
        <Box
          key={i}
          sx={{
            bgcolor: lineColor,
            borderRadius: 1,
            minHeight: '100%',
          }}
        />
      ))}
    </Box>
  );
};

export default WidgetGrid;
